/** Four customer mains + plantation crop sub-role. Mirrors FastAPI `app.core.audience`. */

export const MAIN_CUSTOMER_ROLES = [
  'project_developer',
  'plantation_owner',
  'lgu',
  'blo',
] as const;

export type MainCustomerRole = (typeof MAIN_CUSTOMER_ROLES)[number];

export const PLANTATION_SUB_ROLES = ['banana', 'coconut'] as const;
export type PlantationSubRole = (typeof PLANTATION_SUB_ROLES)[number];

export const LAYER_TAGS = ['all', 'carbon', 'banana', 'coconut', 'lgu', 'blo'] as const;
export type LayerTag = (typeof LAYER_TAGS)[number];
const LAYER_TAG_SPECIFICS = ['carbon', 'banana', 'coconut', 'lgu', 'blo'] as const;

export const ALERT_TAGS = ['all', 'rice', 'banana', 'coconut'] as const;
export type AlertTag = (typeof ALERT_TAGS)[number];

export const ROLE_DISPLAY_LABELS: Record<string, string> = {
  admin: 'Admin',
  project_developer: 'Carbon Project Developer',
  plantation_owner: 'Plantation owner',
  lgu: 'LGU',
  blo: 'BLO (big land owner)',
  user: 'User · legacy',
  staff: 'Staff · legacy',
};

export const LAYER_TAG_LABELS: Record<LayerTag, string> = {
  all: 'All customers',
  carbon: 'Carbon / project developer',
  banana: 'Banana plantation',
  coconut: 'Coconut plantation',
  lgu: 'LGU',
  blo: 'BLO',
};

export const ALERT_TAG_LABELS: Record<AlertTag, string> = {
  all: 'All customers',
  rice: 'Rice (BLO)',
  banana: 'Banana plantation',
  coconut: 'Coconut plantation',
};

export const SIGNUP_ROLE_OPTIONS: { value: MainCustomerRole; label: string }[] = [
  { value: 'blo', label: 'BLO (big land owner)' },
  { value: 'plantation_owner', label: 'Plantation owner' },
  { value: 'lgu', label: 'LGU' },
  { value: 'project_developer', label: 'Carbon Project Developer' },
];

export const PLANTATION_SUB_ROLE_OPTIONS: { value: PlantationSubRole; label: string }[] = [
  { value: 'banana', label: 'Banana' },
  { value: 'coconut', label: 'Coconut' },
];

const LEGACY_ROLE_MAP: Record<string, string> = {
  farm: 'blo',
  banana_plantation: 'plantation_owner',
  plantation: 'plantation_owner',
  municipal: 'lgu',
};

export function coerceRoleValue(role: string | undefined | null): string {
  const s = (role || 'blo').trim().toLowerCase();
  return LEGACY_ROLE_MAP[s] || s;
}

export function normalizeSubRole(role: string | undefined | null, subRole: string | undefined | null): PlantationSubRole | null {
  if (coerceRoleValue(role) !== 'plantation_owner') return null;
  const s = (subRole || '').trim().toLowerCase();
  if (s === 'banana' || s === 'coconut') return s;
  return null;
}

export function layerAudienceKeys(role: string | undefined | null, subRole?: string | null): Set<LayerTag> {
  let r = coerceRoleValue(role);
  if (r === 'user' || r === 'staff') r = 'blo';
  if (r === 'project_developer') return new Set(['all', 'carbon']);
  if (r === 'plantation_owner') {
    const sub = normalizeSubRole(r, subRole) || 'banana';
    return new Set(['all', sub]);
  }
  if (r === 'lgu') return new Set(['all', 'lgu']);
  if (r === 'blo') return new Set(['all', 'blo']);
  return new Set();
}

export function normalizeLayerTags(tags: string[] | undefined | null): LayerTag[] {
  const out: LayerTag[] = [];
  const seen = new Set<string>();
  let hasAll = false;
  for (const raw of tags || []) {
    const tag = raw.trim().toLowerCase();
    if (tag === 'all' || tag === 'common') {
      hasAll = true;
      continue;
    }
    if ((LAYER_TAGS as readonly string[]).includes(tag) && !seen.has(tag)) {
      seen.add(tag);
      out.push(tag as LayerTag);
    }
  }
  if (hasAll) return ['all'];
  return out;
}

/** Editor: ticking All checks every audience box. */
export function expandLayerTagsForEditor(tags: string[] | undefined | null): LayerTag[] {
  const normalized = normalizeLayerTags(tags);
  if (normalized.includes('all')) return [...LAYER_TAGS];
  return normalized;
}

/** Persist All as a single tag, not every box. */
export function collapseLayerTagsForSave(tags: string[] | undefined | null): LayerTag[] {
  const expanded = expandLayerTagsForEditor(tags);
  if (expanded.includes('all') || LAYER_TAG_SPECIFICS.every((t) => expanded.includes(t))) {
    return ['all'];
  }
  return expanded.filter((t) => t !== 'all');
}

export function toggleLayerTag(current: string[] | undefined | null, tag: string): LayerTag[] {
  const expanded = new Set(expandLayerTagsForEditor(current));
  if (tag === 'all' || tag === 'common') {
    return expanded.has('all') ? [] : [...LAYER_TAGS];
  }
  if (!(LAYER_TAGS as readonly string[]).includes(tag)) return [...expanded];
  if (expanded.has(tag as LayerTag)) expanded.delete(tag as LayerTag);
  else expanded.add(tag as LayerTag);
  expanded.delete('all');
  if (LAYER_TAG_SPECIFICS.every((t) => expanded.has(t))) return [...LAYER_TAGS];
  return LAYER_TAGS.filter((t) => t !== 'all' && expanded.has(t));
}

export function alertAudienceKeys(role: string | undefined | null, subRole?: string | null): Set<AlertTag> {
  let r = coerceRoleValue(role);
  if (r === 'user' || r === 'staff') r = 'blo';
  const keys = new Set<AlertTag>(['all']);
  if (r === 'plantation_owner') {
    keys.add(normalizeSubRole(r, subRole) || 'banana');
    return keys;
  }
  if (r === 'blo' || r === 'lgu') {
    keys.add('rice');
    return keys;
  }
  return keys;
}

export function whoSeesLayerTags(tags: string[]): string[] {
  const set = new Set(normalizeLayerTags(tags));
  const rows: { keys: Set<LayerTag>; label: string }[] = [
    { keys: layerAudienceKeys('project_developer'), label: 'Carbon Project Developer' },
    { keys: layerAudienceKeys('plantation_owner', 'banana'), label: 'Plantation owner · banana' },
    { keys: layerAudienceKeys('plantation_owner', 'coconut'), label: 'Plantation owner · coconut' },
    { keys: layerAudienceKeys('lgu'), label: 'LGU' },
    { keys: layerAudienceKeys('blo'), label: 'BLO (big land owner)' },
  ];
  return rows.filter((row) => [...set].some((t) => row.keys.has(t))).map((row) => row.label);
}

const ALERT_TYPE_TAGS: Record<string, AlertTag[]> = {
  vegetation: ['all'],
  moisture: ['all'],
  drought: ['all'],
  weather: ['all'],
  weather_rain: ['all'],
  weather_heat: ['all'],
  weather_drought: ['all'],
  forecast_trend: ['all'],
  rain_trend: ['all'],
  heat_trend: ['all'],
  cadi: ['all'],
  rice_blast: ['rice'],
  bacterial_leaf_blight: ['rice'],
  brown_planthopper: ['rice'],
  stem_borer: ['rice'],
  bsri: ['banana'],
  black_sigatoka: ['banana'],
  sigatoka: ['banana'],
};

export function alertTagsForType(alertType: string | undefined | null, pestKey?: string | null): AlertTag[] {
  const key = (pestKey || alertType || '').trim().toLowerCase();
  if (ALERT_TYPE_TAGS[key]) return ALERT_TYPE_TAGS[key];
  if (key.startsWith('rice_') || key.includes('rice')) return ['rice'];
  if (key.includes('sigatoka') || key.includes('banana') || key === 'bsri') return ['banana'];
  if (key.includes('coconut')) return ['coconut'];
  return ['all'];
}

export function userReceivesAlert(
  user: { role?: string; sub_role?: string | null } | null | undefined,
  tags: string[] | undefined | null
): boolean {
  const audience = alertAudienceKeys(user?.role, user?.sub_role);
  const normalized = (tags && tags.length ? tags : ['all']) as AlertTag[];
  return normalized.some((t) => audience.has(t as AlertTag));
}

export function accountTypeLabel(role: string | undefined | null, subRole?: string | null): string {
  const r = coerceRoleValue(role);
  const base = ROLE_DISPLAY_LABELS[r] || r;
  const sub = normalizeSubRole(r, subRole);
  if (sub) return `${base} · ${sub === 'banana' ? 'Banana' : 'Coconut'}`;
  return base;
}
